import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import AddIcon from '../../../../assets/customSVG/AddIcon';
import Download from '../../../../assets/customSVG/campaigns/Download';
import { ICONS } from '../../../../assets/icons';
import useTags from '../../../../hooks/useTags';
import {
  setFilters,
  setProfileAttributes,
  setProfileAttributesLoading,
  updateFilterLogic,
} from '../../../../reduxToolkit/crmSlice';
import {
  crmFontColor,
  filterKeys,
  mainFilters,
  primaryColor,
  getAllFilterKeys,
  getProfileAttributeFilterConfig,
  getProfileAttributeFieldName,
} from '../../constants';
import { convertName } from '../../helpers';
import {
  crmListShow,
  crmReportStatus,
  crmReportUserSeen,
  AttributeList,
} from '../../api/Api';
import { useAspSelector } from '../../../../../src/test/jest-redux-hooks';
import Modal from '../commonComponents/ParentPositionedModel';
import Button from '../commonComponents/button';
import FilterModelContent from './mainFilters/FilterModelContent';
import RenderFilters from './mainFilters/RenderFilters';
import ToastMain from '../../../../components/commonComponents/toast/ToastMain';
import AudienceSetFiltersView from './AudienceSetFiltersView';

export const MainFilter = ({
  onFilterChange,
  crmCount,
  crmSearch,
  activeFilter,
  selectedItems = [],
  selectAll = false,
}) => {
  const [active, setActive] = useState(false);
  const { tagList } = useSelector((store) => store.crm);
  const { currentBrand } = useAspSelector((state) => state.app);
  const [reportStatus, setReportStatus] = useState(true);
  const [operator, setOperator] = useState({
    isModalOpen: false,
    operator: 'and',
  }); // and | or.
  const [selectedFilter, setSelectedFilter] = useState({
    filterName: 'name',
    field: '',
    fieldValue: '',
  });
  // useState for toast
  const [toastState, setToastState] = useState({
    show: false,
    message: '',
    type: 'Success',
  });

  const dispatch = useDispatch();
  const { filters, profileAttributes } = useSelector((store) => store.crm);

  useEffect(() => {
    onFilterChange(filters);
  }, [filters, onFilterChange]);

  // NEW: Fetch profile attributes on component mount
  useEffect(() => {
    const loadProfileAttributes = async () => {
      if (!currentBrand?.brand_id) {
        return;
      }

      if (profileAttributes.length > 0) {
        return;
      }

      try {
        dispatch(setProfileAttributesLoading(true));
        const response = await AttributeList(currentBrand.brand_id);

        // Extract only attributes with types (not scopes)
        const allAttributes = response.data.scopes_and_attributes_list
          .flatMap((scope) => scope.profile_attributes || [])
          .map((attr) => ({
            id: attr.id,
            name: attr.name,
            label: attr.label,
            attribute_type: attr.attribute_type, // STRING, INTEGER, BOOLEAN, DATE
            default: attr.default,
          }));

        // Remove duplicates based on 'id' (in case same attribute appears in multiple scopes)
        const uniqueAttributes = allAttributes.filter(
          (attr, index, self) =>
            index === self.findIndex((a) => a.id === attr.id),
        );

        dispatch(setProfileAttributes(uniqueAttributes));
      } catch (error) {
        dispatch(setProfileAttributesLoading(false));
      }
    };

    loadProfileAttributes();
  }, [currentBrand?.id, dispatch, profileAttributes.length]);

  const selectedTags = useMemo(() => {
    if (selectedFilter.filterName === 'tags') {
      if (!Array.isArray(selectedFilter.fieldValue)) return [];
      return selectedFilter.fieldValue.map((item) => item.id);
    }
    return [];
  }, [selectedFilter.fieldValue]);
  const { tags } = useTags(selectedTags);

  function handleClick() {
    setActive(!active);
    setOperator((prev) => {
      return { ...prev, operator: 'and' };
    });
  }

  const handleChangeFilter = (item) => {
    // NEW: Check if this is a profile attribute
    const isProfileAttribute = item?.startsWith('attr_');

    let filterType = 'text'; // default

    if (isProfileAttribute) {
      // Get attribute type from profile attributes
      const attrId = parseInt(item.replace('attr_', ''));
      const attribute = profileAttributes.find((attr) => attr.id === attrId);

      if (attribute) {
        const filterConfig = getProfileAttributeFilterConfig(
          attribute.attribute_type,
        );

        // Safety check: ensure filterConfig and props exist
        if (filterConfig && filterConfig.props && filterConfig.props.type) {
          filterType = filterConfig.props.type;
        } else {
          console.warn('Invalid filter config for attribute:', attribute);
          filterType = 'text'; // fallback
        }
      } else {
        console.warn('Profile attribute not found for item:', item);
      }
    } else {
      // Standard filter - get from mainFilters
      filterType = mainFilters[item]?.props?.type || 'text';
      console.log('Standard filter type:', filterType);
    }

    // Handle dropdown and tags filters differently (need array fieldValue)
    if (['dropdown', 'tags'].includes(filterType)) {
      setSelectedFilter({
        filterName: item,
        field: '',
        fieldValue: [],
        dropdownOpen: null,
      });
      return;
    }

    setSelectedFilter({ filterName: item, field: '', fieldValue: '' });
  };

  const fetchReportStatus = () => {
    crmReportStatus(currentBrand?.brand_id).then((res) => {
      if (res?.status === 200) {
        const report = res?.data?.crm_report;
        report ? setReportStatus(report?.user_seen) : setReportStatus(true);
        if (report?.status === 10) {
          setToastState({
            show: true,
            message: 'Report has been sent to your email, successfully',
            type: 'Success',
            duration: 3000,
          });

          const payload = { crm_report_id: report?.id };
          crmReportUserSeen(currentBrand?.brand_id, payload).then((res) => {
            if (res?.status === 200) {
            }
          });
        } else if (report?.status === -1) {
          setToastState({
            show: true,
            message:
              'Report has not been sent due to an error, please try again',
            type: 'Error',
            duration: 3000,
          });

          const payload = { crm_report_id: report?.id };
          crmReportUserSeen(currentBrand?.brand_id, payload).then((res) => {
            if (res?.status === 200) {
            }
          });
        } else if (
          report?.status === 0 ||
          report?.status === 3 ||
          report?.status === 5
        ) {
        }
      }
    });
  };

  // // useEffect for report status
  // useEffect(() => {
  //   let interval;
  //   let timeout;

  //   fetchReportStatus();
  //   interval = setInterval(fetchReportStatus, 5000);

  //   return () => {
  //     clearInterval(interval);
  //     clearTimeout(timeout);
  //   };
  // }, [currentBrand]);

  const brandId = currentBrand?.brand_id;
  // generate report function
  // const generateReport = async () => {
  //   try {
  //     if (brandId) {
  //       const selectedIds = selectedItems.filter(i => i.selected).map(i => i.id);
  //       const deselectedIds = selectedItems.filter(i => !i.selected).map(i => i.id);

  //       // Check if either select all is true or at least one contact is selected
  //       if (!selectAll && selectedIds.length === 0) {
  //         setToastState({
  //           show: true,
  //           message: "Please select at least one contact or enable select all",
  //           type: "Error",
  //           duration: 2000
  //         });
  //         return;
  //       }

  //       const queryParams = {
  //         limit: crmCount,
  //         page: 1,
  //         search: crmSearch,
  //         filters: Object.keys(filters)?.length > 0 ? filters : undefined,
  //         send_report: true,
  //         tag_id: activeFilter?.tag ? activeFilter?.id : undefined,
  //         user_id: activeFilter?.user_name ? activeFilter?.id : undefined,
  //         contact_origin: activeFilter?.name ? activeFilter?.name : undefined,
  //         select_all: selectAll,
  //         contact_person_ids: selectAll ? deselectedIds : selectedIds,
  //       }
  //       const res = await crmListShow(brandId, queryParams)
  //       if (res?.status === 200) {
  //         setToastState({
  //           show: true,
  //           message: "Report has been sent to your email, shortly",
  //           type: "Success",
  //           duration: 3000
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error fetching crm contacts:", error);
  //   }
  // }

  // Check if we're viewing an audience set
  const isAudienceSet = activeFilter?.type === 'audience_set';

  const renderFilterModal = () => (
    <Modal
      open={active}
      styles={{ content: { padding: 0 } }}
      onOpenChange={handleClick}
      rootclassName="max-w-[35vw] crm-dropdown rounded-2xl overflow-hidden"
      Button={
        <div className="flex items-center cursor-pointer whitespace-nowrap py-[.2vw] rounded-md">
          <AddIcon stroke="var(--textBlack)" width="24" height="24" />
          <span className="text-[.8vw] font-[500]">Add Filter</span>
        </div>
      }
      sideBarMainClassName="p-[0vw_!important]"
      ModelSideNav={
        <div className="space-y-[12px] max-h-[50vh] overflow-scroll scroll_visible *:py-[8px] text-[14px] *:ps-[12px] px-[14px] py-[12px]">
          {getAllFilterKeys(profileAttributes).map(
            ({ name: filterName, label, isProfileAttribute }) => (
              <p
                key={filterName}
                className={`w-full break-words !p-[8px] ${
                  selectedFilter.filterName === filterName
                    ? `bg-[${primaryColor}] text-white`
                    : 'hover:bg-gray-200'
                } rounded-md capitalize cursor-pointer duration-200`}
                onClick={() => handleChangeFilter(filterName)}
              >
                {isProfileAttribute ? label : convertName(filterName)}
              </p>
            ),
          )}
        </div>
      }
    >
      <div className="grid grid-rows-[6fr,1fr] h-full scroll_visible">
        <div className="space-y-[.5vw] max-h-[43vh] overflow-scroll scroll_visible *:w-[5vw]- px-[1vw] pt-[1vw]">
          {(() => {
            // NEW: Check if selected filter is a profile attribute
            const isProfileAttr =
              selectedFilter?.filterName?.startsWith('attr_');
            let filterConfig;

            if (isProfileAttr) {
              // Get config for profile attribute
              const attrId = parseInt(
                selectedFilter.filterName.replace('attr_', ''),
              );
              const attribute = profileAttributes.find(
                (attr) => attr.id === attrId,
              );

              if (attribute) {
                filterConfig = getProfileAttributeFilterConfig(
                  attribute.attribute_type,
                );
              } else {
                return (
                  <div className="text-center text-gray-500">
                    Attribute not found
                  </div>
                );
              }
            } else {
              // Get config for standard filter
              filterConfig = mainFilters?.[selectedFilter?.filterName];

              if (!filterConfig) {
                return (
                  <div className="text-center text-gray-500">
                    Filter configuration not found
                  </div>
                );
              }
            }
            return Object.entries(filterConfig?.filters || {}).map(
              ([filterKey]) => {
                const props = filterConfig.props;
                const isOnlyCheckBox =
                  props?.check?.includes(filterKey) || false;
                return (
                  <FilterModelContent
                    key={filterKey}
                    filterKey={filterKey}
                    type={props?.type}
                    tagList={tagList}
                    inputType={props?.inputType}
                    propsData={props?.data}
                    onlySelectFields={props?.check || []}
                    isOnlyCheckBox={isOnlyCheckBox}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    profileAttributes={profileAttributes}
                  />
                );
              },
            );
          })()}
        </div>
        <div className="flex align-center justify-end sticky bottom-0 bg-white gap-[.5vw] w-full border-t border-t-gray-200 py-[.5vw] pe-[1vw]">
          <Button
            onClick={handleClick}
            label="Cancel"
            classNames="text-black"
            color="white"
          />
          <Button
            disabled={
              !selectedFilter.fieldValue ||
              selectedFilter.fieldValue.length === 0
            }
            onClick={() => {
              if (
                !selectedFilter.fieldValue &&
                selectedFilter.fieldValue.length === 0
              ) {
                return;
              }

              // IMPORTANT: Capture the operator BEFORE resetting it
              const currentOperator = operator.operator;

              // If there are existing filters, update the LAST filter's logic to connect it to this new one
              if (filters.length > 0) {
                const lastFilter = filters[filters.length - 1];
                dispatch(
                  updateFilterLogic({
                    id: lastFilter.id,
                    logic: currentOperator,
                  }),
                );
              }

              setActive(false);
              setOperator((prev) => ({ ...prev, operator: 'and' }));
              setSelectedFilter({
                filterName: 'name',
                field: '',
                fieldValue: '',
              });

              // Get the correct field name for profile attributes
              // For special fields (date_of_birth, age, gender, tags), use the actual field name
              // For regular profile attributes, use attr_<id> format
              const fieldNameToUse = getProfileAttributeFieldName(
                selectedFilter.filterName,
                profileAttributes
              );

              dispatch(
                setFilters({
                  key: fieldNameToUse,
                  filter: { [selectedFilter.field]: selectedFilter.fieldValue },
                  value: {
                    selectedFilter: fieldNameToUse,
                    selectedField: selectedFilter.field,
                    fieldValue: selectedFilter.fieldValue,
                  },
                  operator: 'and', // Default for connecting to future filters
                }),
              );
            }}
            label="Apply"
            classNames="text-[.5]"
          />
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      {/* Toast for report status */}
      {toastState.show && (
        <div className="fixed top-4 right-4 z-50">
          <ToastMain
            key={Date.now()}
            message={toastState.message}
            type={toastState.type}
            handleClick={() =>
              setToastState((prev) => ({ ...prev, show: false }))
            }
          />
        </div>
      )}

      <div className="mx-[1vw] border-b border-b-[var(--border-50)] py-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex-1 flex items-center gap-4">
            {isAudienceSet ? (
              // Show audience set filters in read-only mode
              <div className="flex-1">
                <AudienceSetFiltersView filters={activeFilter?.filters} />
              </div>
            ) : (
              // Show regular filters with add/remove functionality
              <div className="flex-1 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {filters.length > 0 && (
                    <RenderFilters
                      filters={filters || []}
                      operator={operator}
                      setOperator={setOperator}
                      handleAddNewFilter={handleClick}
                      profileAttributes={profileAttributes}
                    />
                  )}
                  {renderFilterModal()}
                </div>
                {/* Download Reports Button */}
                {/* <div
                  className={`group p-5 flex-row align-center h-fit ${!reportStatus ? 'cursor-not-allowed' : 'pointer hover:bg-[var(--new-primary-bg)]'} b-radius-6 border border-[var(--border-input)] text-[1rem] font-[500] text-[var(--font-600)] hover:text-[var(--new-primary)] hover:border-[var(--new-primary-bg)]`}
                  onClick={() => {
                    if (reportStatus) {
                      generateReport();
                    }
                  }}
                >
                  <Tooltip title={!reportStatus ? "Report is generating, please wait" : "Download Reports"} placement="bottom">
                    <div className={`flex items-center ${!reportStatus ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      <Download
                        className="stroke-[var(--font-600)] group-hover:stroke-[var(--new-primary)] h-[20px] w-[20px]"
                        color=""
                      />
                    </div>
                  </Tooltip>
                </div> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
